import { AlertTriangle, Check, Loader2, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  useSupersededOrders,
  type SupersededOrder,
} from '@/hooks/admin/useSupersededOrders';

/**
 * Orders this customer replaced by changing them — and whether the old
 * Service Fusion job has actually gone away yet.
 *
 * ⚠ There is deliberately nothing to click. Service Fusion has no write API
 * for jobs, so the deletion happens in SF's own UI; and the row clears itself
 * when order-lifecycle-check next reads that job and finds it gone. A "mark
 * handled" button would let somebody clear the flag while the duplicate job is
 * still live, which is the single failure this panel exists to catch.
 */

function fmtWhen(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function Row({ row, open }: { row: SupersededOrder; open: boolean }) {
  return (
    <li
      className={cn(
        'rounded-lg border p-3 text-sm',
        open ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200 bg-white',
      )}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {open ? (
          <Trash2 className="h-4 w-4 shrink-0 text-amber-600" aria-hidden />
        ) : (
          <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
        )}
        <span className="font-medium text-slate-900">
          {open ? 'Delete SF job ' : 'SF job '}
          {row.sf_job_id ?? '—'}
        </span>
        {row.order_number ? (
          <span className="text-slate-500">was {row.order_number}</span>
        ) : null}
        <span className="ml-auto text-xs text-slate-500">{fmtWhen(row.superseded_at)}</span>
      </div>

      <p className="mt-1 text-slate-700">
        {row.replaced_by ? (
          <>
            Replaced by{' '}
            <span className="font-medium">{row.replaced_by.order_number ?? 'a new order'}</span>
            {row.replaced_by.sf_job_id ? ` · SF ${row.replaced_by.sf_job_id}` : ''} — that one is
            the order to fill.
          </>
        ) : (
          'Replaced by a newer order.'
        )}
      </p>

      {open ? (
        <p className="mt-1 text-xs text-amber-800">
          {row.dispatch_emailed
            ? 'Dispatch was emailed. This clears on its own once the old job is deleted or cancelled in Service Fusion.'
            : '⚠ The dispatch email never went out — tell whoever runs Service Fusion to delete this job.'}
        </p>
      ) : (
        <p className="mt-1 text-xs text-slate-500">Old job gone — nothing to do.</p>
      )}
    </li>
  );
}

export function SupersededOrders({
  customerId,
  customerName,
}: {
  customerId: string;
  customerName: string;
}) {
  const { data, isLoading, error } = useSupersededOrders(customerId);
  const open = data?.open ?? [];
  const resolved = data?.resolved ?? [];

  // Quiet on the accounts that have never changed an order, rather than an
  // empty card on all ~150 of them (the relevance-not-uniformity rule).
  if (!isLoading && !error && open.length === 0 && resolved.length === 0) return null;

  return (
    <div className="max-w-2xl">
      <h2 className="text-base font-semibold text-slate-900">Changed orders</h2>
      <p className="mt-1 text-sm text-slate-600">
        When {customerName} changes an order we place a new one and ask dispatch to delete the old
        Service Fusion job. Until that job is gone, two jobs exist for one delivery.
      </p>

      {isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
        </div>
      ) : error ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error instanceof Error ? error.message : 'Could not load replaced orders.'}</span>
        </div>
      ) : (
        <>
          {open.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {open.map((r) => (
                <Row key={r.id} row={r} open />
              ))}
            </ul>
          ) : null}
          {resolved.length > 0 ? (
            <>
              <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Done
              </h3>
              <ul className="mt-2 space-y-2">
                {resolved.map((r) => (
                  <Row key={r.id} row={r} open={false} />
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
